import { Skeleton, Stack } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { DestinationViewType } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { Suspense } from "react";

import { SectionCard } from "../../../../Manifests/sections/SectionCard";
import { SectionHeader } from "../../../../Manifests/sections/SectionHeader";
import { useWasteFacilityState } from "../../WasteFacilityContext";
import { DestinationCards } from "./DestinationCards";

interface DestinationsDataGridProps {
  wasteStreamId: string;
}

// Prod streams have ~1 destination and exactly 1 management method (p95 = 1), so
// destinations render as compact cards — each carrying its management method +
// description — rather than a datagrid. Streams with many (>8) destinations fall
// back to a grid inside DestinationCards.
export const DestinationsDataGrid = ({ wasteStreamId }: DestinationsDataGridProps) => {
  const { tenantId } = useTenant();
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();

  // One CSV export at the finest grain (destination × management method), which
  // subsumes the per-destination and per-method groupings. Honors the current
  // date/view filter.
  const exportButton = (
    <ExportDataButton
      exportType="wasteStreamDestinations"
      fileName="waste-stream-destinations"
      tenantId={tenantId ?? ""}
      filter={{
        viewType: DestinationViewType.DestinationsByManagementMethod,
        wasteStreamId,
        search: "",
        view: deferredFilters.view,
        startDate: deferredFilters.startDate,
        endDate: deferredFilters.endDate,
      }}
      iconOnly
    />
  );

  return (
    <Stack spacing={3}>
      <SectionCard>
        <SectionHeader title="Destinations and management methods" action={exportButton} />
        <Suspense fallback={<Skeleton variant="rectangular" height={120} />}>
          <DestinationCards wasteStreamId={wasteStreamId} />
        </Suspense>
      </SectionCard>
    </Stack>
  );
};
